/**
 * One place to decide how often the app re-reads server state.
 *
 * Built on react-query's own `refetchInterval` rather than a bespoke setInterval hook, because
 * the library already solves the parts that are easy to get wrong by hand:
 *
 *   - it refetches per query KEY, so a list that is filtered, searched or paginated polls its own
 *     current view and cannot reset the user back to an unfiltered page one;
 *   - it will not stack a refetch on top of one already in flight;
 *   - two components mounting the same key share a single request;
 *   - `isLoading` stays false once there is cached data, so a background refetch updates rows in
 *     place and never re-paints a skeleton.
 *
 * Tab visibility is handled by the library too: react-query's focusManager listens to
 * `visibilitychange`, and with `refetchIntervalInBackground: false` the interval does not fire
 * while the tab is hidden. Returning to the tab restarts the timer AND — via the global
 * `refetchOnWindowFocus: true` in main.tsx — fires one immediate catch-up fetch, so the user
 * never waits out an interval to see current data.
 */

/**
 * Message lists: 15s.
 *
 * Anchored to the phone rather than picked for feel. The Android gateway's own sync cadence is
 * 15s (GatewayPrefs.DEFAULT_POLL_INTERVAL_MS), which is the beat on which the server itself
 * learns about most delivery-status changes. Polling faster than the source of truth updates
 * would buy latency we cannot actually deliver, and would only add load.
 */
export const MESSAGE_POLL_MS = 15_000;

/**
 * Dashboard: 20s.
 *
 * Slower on purpose. These are aggregate counters read at a glance, where being a few seconds
 * behind changes no decision the user is about to make. It also sits above the global 15s
 * staleTime, so an interval tick and a focus-triggered refetch do not routinely land back to
 * back on the same second.
 */
export const DASHBOARD_POLL_MS = 20_000;

/**
 * Query options that make a query keep itself current. Spread into `useQuery`.
 *
 * Kept as a function returning a literal rather than a custom hook: polling here is a property
 * of an individual query, not a component-wide behaviour, and wrapping useQuery would hide which
 * queries actually poll.
 */
export function polling(intervalMs: number) {
  return {
    refetchInterval: intervalMs,
    // Already the library default. Stated explicitly because "pauses in a background tab" is a
    // requirement of this feature, not an incidental default worth inheriting silently — if the
    // default ever changed, this line is what keeps the behaviour.
    refetchIntervalInBackground: false,
  } as const;
}
